// ─────────────────────────────────────────────────────────────────
// Glass-i — Other Charges Master API client (Stage 6OC.1).
//
// Thin axios wrappers around /api/erp/other-charges/*:
//   - rule CRUD (/rules)
//   - calculation preview + audit-save (/preview, /audit-save)
//   - calculation audit history (/calculation-audits)
//   - master-driven dropdowns (/dropdowns)
//
// Mirrors the supplierPaymentApi / thicknessMasterApi shape — the
// `unwrap` helper handles both the {data:{data:...}} envelope used by
// most endpoints and the bare {data:...} shape.
//
// The backend rule-engine
// (backend/src/services/erp/otherCharges/otherChargesCalculation.service.js)
// remains the source of truth for ALL charge math; the frontend helper
// (otherChargesHelpers.js) only mirrors it for instant preview.
//
// Dropdown business values (material types / glass types / thicknesses /
// categories) are NEVER hardcoded — they come exclusively from
// getDropdowns() at runtime.
// ─────────────────────────────────────────────────────────────────

import client from '../../../api/client';

const BASE = '/api/erp/other-charges';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

export const otherChargesApi = {
  // ── Rule CRUD ─────────────────────────────────────────────────
  listRules:   (params = {}) =>
    client.get(`${BASE}/rules`, { params }).then(unwrap),
  getRule:     (id) =>
    client.get(`${BASE}/rules/${encodeURIComponent(id)}`).then(unwrap),
  createRule:  (payload = {}) =>
    client.post(`${BASE}/rules`, payload).then(unwrap),
  updateRule:  (id, payload = {}) =>
    client.put(`${BASE}/rules/${encodeURIComponent(id)}`, payload).then(unwrap),
  deleteRule:  (id) =>
    client.delete(`${BASE}/rules/${encodeURIComponent(id)}`).then(unwrap),
  // Reorder the sequence of execution. `orderedIds` is the COMPLETE list of
  // rule ids in the desired order; the backend re-stamps sort_order to match.
  reorderRules: (orderedIds = []) =>
    client.put(`${BASE}/rules/reorder`, { orderedIds }).then(unwrap),

  // ── Calculation preview + audit-save ──────────────────────────
  preview:     (payload = {}) =>
    client.post(`${BASE}/preview`, payload).then(unwrap),
  auditSave:   (payload = {}) =>
    client.post(`${BASE}/audit-save`, payload).then(unwrap),

  // ── Calculation audit history ─────────────────────────────────
  listCalculationAudits: (params = {}) =>
    client.get(`${BASE}/calculation-audits`, { params }).then(unwrap),
  getCalculationAudit:   (id) =>
    client.get(`${BASE}/calculation-audits/${encodeURIComponent(id)}`).then(unwrap),

  // ── Master-driven dropdowns ───────────────────────────────────
  getDropdowns: () =>
    client.get(`${BASE}/dropdowns`).then(unwrap)
};

// ─────────────────────────────────────────────────────────────────
// Safe dropdown loader — never throws. On any failure it returns an
// empty-but-well-shaped payload with `errors.all = true` so the page
// can show a retry state instead of crashing the master form. Mirrors
// the loadOtherChargesDropdownsSafe contract the page relies on.
// ─────────────────────────────────────────────────────────────────
export async function loadOtherChargesDropdownsSafe() {
  try {
    const data = await otherChargesApi.getDropdowns();
    if (!data || typeof data !== 'object') {
      return {
        materialTypes: [],
        glassTypes:    [],
        thicknesses:   [],
        categories:    [],
        polishTypes:   [],
        gstRates:      [],
        errors:        { all: true }
      };
    }
    return {
      materialTypes: Array.isArray(data.materialTypes) ? data.materialTypes : [],
      glassTypes:    Array.isArray(data.glassTypes)    ? data.glassTypes    : [],
      glassFamilies: Array.isArray(data.glassFamilies) ? data.glassFamilies : [],
      thicknesses:   Array.isArray(data.thicknesses)   ? data.thicknesses   : [],
      categories:    Array.isArray(data.categories)    ? data.categories    : [],
      polishTypes:   Array.isArray(data.polishTypes)   ? data.polishTypes   : [],
      gstRates:      Array.isArray(data.gstRates)      ? data.gstRates      : [],
      errors:        data.errors || null
    };
  } catch (_e) {
    return {
      materialTypes: [],
      glassTypes:    [],
      thicknesses:   [],
      categories:    [],
      polishTypes:   [],
      gstRates:      [],
      errors:        { all: true }
    };
  }
}

export default otherChargesApi;
